import React, { useMemo } from "react";
import PropTypes from "prop-types";
import {
	Card,
	CardHeader,
	CardBody,
	CardTitle,
	Row,
	Col,
	Alert,
} from "reactstrap";
import shallow from "zustand/shallow";
import dayjs from "dayjs";

// Stores / hooks (adjust paths as needed)
import useCurrentDealStore from "stores/DealStore";
import useCurrentSaleStore from "stores/SaleStore";
import useCurrentLeaseStore from "stores/LeaseStore";
import useBuildDeal from "../../hooks/useBuildDeal";

// Reuse existing review blocks
import ReviewCustomer from "./ReviewCustomer";
import ReviewVehicle from "./ReviewVehicle";
import ReviewRow from "./ReviewRow";
import { SaleType } from "../../../../constants/Constants";

// ----------------- utils -----------------
const fmt = {
	usd: (n) =>
		Number.isFinite(Number(n))
			? Number(n).toLocaleString("en-US", {
					style: "currency",
					currency: "USD",
			  })
			: "—",
	pct: (n, digits = 2) => {
		if (n == null || isNaN(n)) return "—";
		const val = Number(n);
		return `${(val * 100).toFixed(digits)}%`;
	},
	text: (v) => (v == null || v === "" ? "—" : String(v)),
	date: (v) => (v == null || v === "" ? "—" : dayjs(v).format("MM/DD/YYYY")),
};
const safeNum = (v, d = 0) => {
	const n = Number(v);
	return Number.isFinite(n) ? n : d;
};

const fCurrency = (n) =>
	safeNum(n).toLocaleString("en-US", { style: "currency", currency: "USD" });

// Normalize APR (handles 0.1795 and 17.95)
const aprToPercentNumber = (aprRaw) => {
	const n = Number(aprRaw);
	if (!isFinite(n)) return null;
	return n < 1 ? n * 100 : n;
};
const KV = ({
	label,
	value,
	isCurrency = false,
	isPercent = false,
	right = true,
	colSize = "6",
	isPhone = false,
}) => (
	<>
		<Col xs={colSize}>{label}</Col>
		<Col className={right ? "text-right" : ""}>
			{isCurrency ? (
				<NumberFormat
					value={value}
					thousandSeparator={true}
					decimalScale={2}
					fixedDecimalScale={true}
					prefix={"$"}
					isNumericString={true}
					displayType="text"
				/>
			) : isPercent ? (
				<NumberFormat
					value={value * 100}
					decimalScale={2}
					fixedDecimalScale={true}
					suffix={"%"}
					isNumericString={true}
					displayType="text"
				/>
			) : isPhone ? (
				<NumberFormat
					value={value}
					format="### ###-####"
					isNumericString={true}
					displayType="text"
				/>
			) : (
				value
			)}
		</Col>
	</>
);
// Lightweight in-place emphasis block (keeps position)
const EmphasisRow = ({
	label,
	valueNode,
	rightClass = "text-primary",
	sizeClass = "h4",
}) => (
	<div className="d-flex justify-content-between align-items-center p-2 my-2 rounded">
		<div className={`${sizeClass} text-muted"`}>{label}</div>
		<div className={`${sizeClass} mb-0 font-weight-bold ${rightClass}`}>
			{valueNode}
		</div>
	</div>
);

// ----------------- component -----------------
const LeaseReview = ({ onPrint }) => {
	const data = useBuildDeal();

	// Fallback to stores when hook data not present
	const deal = useCurrentDealStore((s) => s.deal, shallow);
	const sale = useCurrentSaleStore((s) => s, shallow);
	const lease = useCurrentLeaseStore((s) => s, shallow);

	// const deal = data?.deal ?? storeDeal ?? {};
	// const sale = data?.deal?.sale ?? storeSale ?? {};
	// const lease = data?.deal?.lease ?? storeLease ?? {};
	// const recap = data?.deal?.recap ?? {};
	// const lender = data?.lender ?? {};

	//const derived = useMemo(() => {
	// const mf = safeNum(lease.moneyFactor, 0);
	// const aprStoredPct = aprToPercentNumber(sale.apr); // e.g. 17.95
	// const aprFromMF = mf ? mf * 2400 : null; // APR% from MF
	// const aprDiff =
	// 	aprStoredPct != null && aprFromMF != null
	// 		? Math.abs(aprStoredPct - aprFromMF)
	// 		: null;

	// const msrp = safeNum(lease.msrp);
	// const residualFactor = safeNum(lease.residualFactor);
	// const residualValueFromFactor = isFinite(msrp * residualFactor)
	// 	? msrp * residualFactor
	// 	: null;
	// const residualValue = safeNum(lease.residualValue);

	// const basePayment = safeNum(lease.basePayment);
	// const monthlyTax = safeNum(lease.monthlyTax);
	// const monthlyPmt = safeNum(lease.monthlyPmt || lease.payment);
	// const pmtDiff = Math.abs(basePayment + monthlyTax - monthlyPmt);

	// const term = safeNum(lease.term || sale.term);
	// const totLease = safeNum(lease.totalLease);
	// const recomputedTotLease = isFinite(monthlyPmt * term)
	// 	? monthlyPmt * term
	// 	: null;
	// const totLeaseDiff =
	// 	recomputedTotLease != null
	// 		? Math.abs(totLease - recomputedTotLease)
	// 		: null;

	// const taxRateSale = safeNum(sale.taxRate);
	// const leaseTotTax = safeNum(lease.totTax ?? lease.totalTax);
	// const saleSalesTax = safeNum(sale.salesTax ?? sale.tax);

	//	return {
	// mf,
	// aprStoredPct,
	// aprFromMF,
	// aprDiff,
	// msrp,
	// residualFactor,
	// residualValue,
	// residualValueFromFactor,
	// basePayment,
	// monthlyTax,
	// monthlyPmt,
	// pmtDiff,
	// term,
	// totLease,
	// recomputedTotLease,
	// totLeaseDiff,
	// taxRateSale,
	// leaseTotTax,
	// saleSalesTax,
	//	};
	//}, [deal.dealSettings, lease, sale]);

	// const warnings = useMemo(() => {
	// 	const w = [];
	// 	if (derived.aprDiff != null && derived.aprDiff > 0.1) {
	// 		w.push(
	// 			`APR mismatch: stored ${derived.aprStoredPct?.toFixed(
	// 				2
	// 			)}% vs MF-derived ${derived.aprFromMF?.toFixed(2)}%`
	// 		);
	// 	}
	// 	if (
	// 		derived.residualValueFromFactor != null &&
	// 		Math.abs(derived.residualValueFromFactor - derived.residualValue) > 1
	// 	) {
	// 		w.push(
	// 			`Residual mismatch: ${fCurrency(
	// 				derived.residualValue
	// 			)} vs MSRP×ResidualFactor ${fCurrency(derived.residualValueFromFactor)}`
	// 		);
	// 	}
	// 	if (derived.pmtDiff > 0.02) {
	// 		w.push(
	// 			`Payment check: base + tax ≠ monthly payment (${fCurrency(
	// 				derived.basePayment + derived.monthlyTax
	// 			)} ≠ ${fCurrency(derived.monthlyPmt)})`
	// 		);
	// 	}
	// 	if (derived.totLeaseDiff != null && derived.totLeaseDiff > 0.5) {
	// 		w.push(
	// 			`Total lease differs from monthly×term: stored ${fCurrency(
	// 				derived.totLease
	// 			)} vs computed ${fCurrency(derived.recomputedTotLease)}`
	// 		);
	// 	}
	// 	if (
	// 		safeNum(lease.term) &&
	// 		safeNum(sale.term) &&
	// 		safeNum(lease.term) !== safeNum(sale.term)
	// 	) {
	// 		w.push(`Term mismatch: lease ${lease.term} vs sale ${sale.term}`);
	// 	}
	// 	if (
	// 		lease.firstPmtDate &&
	// 		lease.leaseDate &&
	// 		lease.firstPmtDate < lease.leaseDate
	// 	) {
	// 		w.push(
	// 			`First payment date (${fmt.date(
	// 				lease.firstPmtDate
	// 			)}) precedes lease date (${fmt.date(lease.leaseDate)}).`
	// 		);
	// 	}
	// 	if (derived.leaseTotTax === 0 && derived.saleSalesTax > 0) {
	// 		w.push(
	// 			`Lease total tax is 0 but sale sales tax is ${fCurrency(
	// 				derived.saleSalesTax
	// 			)} — confirm tax flow.`
	// 		);
	// 	}
	// 	return w;
	// }, [derived, lease.firstPmtDate, lease.leaseDate, lease.term, sale.term]);

	return (
		<div className="col-lg-8 mx-auto">
			<Card className="mt-4">
				<CardHeader className="d-flex justify-content-between align-items-center">
					<div>
						<h4 className="mb-0">Lease Review</h4>
					</div>
				</CardHeader>

				<CardBody>
					<Row>
						<Col lg="12" className="mb-3">
							<Card>
								<Col lg="3" className="mb-3">
									<ReviewRow label="Lease Number" value={sale.dealNumber} />
									<ReviewRow
										label="Lease Type"
										value={SaleType[sale.saleType].label}
									/>
								</Col>
							</Card>
						</Col>
						<Col lg="12" className="mb-3">
							<Card>
								{/* <div className="text-hint">
									Deal #{deal.ID ?? "—"} • Created {fmt.date(sale.loanDate)}
									{sale.dealNumber ? ` • Deal No. ${sale.dealNumber}` : ""}
									{lender?.name ? ` • Lender: ${fmt.text(lender.name)}` : ""}
								</div> */}
							</Card>
						</Col>
					</Row>

					{/* Top: Customer & Vehicle */}
					<Row>
						<Col lg="6" className="mb-3">
							<ReviewCustomer />
						</Col>
						<Col lg="6" className="mb-3">
							<ReviewVehicle />
						</Col>
					</Row>

					{/* Terms */}
					<Row>
						<Col lg="6" className="mb-3">
							<Card>
								<CardHeader>
									<CardTitle tag="h3">Lease Terms</CardTitle>
								</CardHeader>
								<CardBody>
									<ReviewRow
										label="Term"
										value={`${lease.term || sale.term || "—"} months`}
									/>
									<ReviewRow
										label="Period"
										value={fmt.text(lease.leasePeriod ?? sale.loanPeriod)}
									/>
									<ReviewRow
										label="Lease Date"
										value={fmt.date(lease.leaseDate)}
									/>
									<ReviewRow
										label="Days to 1st Pmt"
										//										value={fmt.text(lease.daysToPay ?? sale.daysToPay)}
										value={lease.daysToPay}
									/>
									<ReviewRow
										label="1st Pmt Date"
										value={fmt.date(lease.firstPmtDate || sale.firstDueDate)}
									/>

									<ReviewRow isCurrency label="MSRP" value={lease.msrp} />
									<ReviewRow
										isPercent
										label="Residual Factor"
										value={lease.residualFactor}
									/>

									{/* Emphasize Residual Value */}
									<EmphasisRow
										label="Residual Value"
										valueNode={fCurrency(lease.residualValue)}
										bg="#eefaf1"
										rightClass="text-success"
										sizeClass="h5"
									/>

									<ReviewRow label="Money Factor" value={lease.moneyFactor} />

									{/* Emphasize APRs */}
									<EmphasisRow
										label="APR"
										valueNode={
											lease.apr != null
												? `${(lease.apr * 100).toFixed(2)}%`
												: "—"
										}
										bg="#eaf6ff"
										rightClass="text-info"
										sizeClass="h5"
									/>

									<ReviewRow
										label="Miles / Year"
										value={fmt.text(lease.milesPerYear)}
									/>
									<ReviewRow
										isCurrency
										label="Per-Mile Charge"
										value={lease.perMileCharge}
									/>
								</CardBody>
							</Card>
						</Col>
						{/* Capitalized */}
						<Col lg="6" className="mb-3">
							<Card>
								<CardHeader>
									<CardTitle tag="h3">Capitalized Costs</CardTitle>
								</CardHeader>
								<CardBody>
									<ReviewRow isCurrency label="Price" value={lease.price} />
									{sale.aftmkt > 0 && (
										<ReviewRow
											isCurrency
											label="Aftermarket"
											value={sale.aftmkt}
										/>
									)}
									{lease.mileageCharge > 0 && (
										<ReviewRow
											isCurrency
											label="Mileage Charge"
											value={lease.mileageCharge}
										/>
									)}
									{lease.capDMVFee && (
										<ReviewRow
											isCurrency
											label="DMV Fee"
											value={sale.totalDMVFees}
										/>
									)}
									{lease.capBankFee && (
										<ReviewRow
											isCurrency
											label="Bank Fee"
											value={lease.bankFee}
										/>
									)}
									{lease.capWarranty && (
										<ReviewRow
											isCurrency
											label="Warranty"
											value={sale.warranty}
										/>
									)}
									{lease.capDocFee && (
										<ReviewRow
											isCurrency
											label="Doc Fee"
											value={lease.docfee}
										/>
									)}
									{lease.capMiscFee && (
										<ReviewRow
											isCurrency
											label="Misc Fee"
											value={lease.miscFee}
										/>
									)}
									{lease.capOtherfee && (
										<ReviewRow
											isCurrency
											label="Other Fee"
											value={lease.otherFee}
										/>
									)}
									{lease.capDispositionFee && (
										<ReviewRow
											isCurrency
											label="Disposition Fee"
											value={lease.disposition}
										/>
									)}
									{lease.capGAPWaiver > 0 && (
										<ReviewRow
											isCurrency
											label="Waiver"
											value={lease.gapWaiver}
										/>
									)}
									{lease.capSmogFees > 0 && (
										<ReviewRow
											isCurrency
											label="Smog Fees"
											value={sale.dealerSmog + sale.stateSmog + sale.smogImpact}
										/>
									)}

									{sale.collisionTotPremium > 0 && (
										<ReviewRow
											isCurrency
											label="collision"
											value={sale.collisionTotPremium}
										/>
									)}
									{sale.payoff > 0 && (
										<ReviewRow
											isCurrency
											label="Trade Payoff"
											value={sale.payoff}
										/>
									)}
									{/* Emphasize Net Cap */}
									<EmphasisRow
										label="Cap Cost (Gross)"
										valueNode={fCurrency(lease.capCost)}
										rightClass="text-dark"
										sizeClass="h6"
										bg="#eaf6ff"
									/>
									<ReviewRow
										isCurrency
										label=" - Reduction"
										value={lease.capReduction}
									/>
									{/* <ReviewRow
										isCurrency
										label="- Net TradeIn"
										value={sale.totalNetTrade}
									/> */}

									<EmphasisRow
										label="Net Cap"
										valueNode={fCurrency(lease.netCap)}
										bg="#f4f4f6"
										rightClass="text-dark"
										sizeClass="h5"
									/>
									{lease.capSalesTax > 0 && (
										<ReviewRow
											isCurrency
											label="Caped Tax"
											value={lease.totTax - lease.downTax - lease.deferredTax}
										/>
									)}

									<EmphasisRow
										label="Total Cap"
										valueNode={fCurrency(
											lease.totTax - lease.downTax + lease.netCap
										)}
										bg="#f4f4f6"
										rightClass="text-dark"
										sizeClass="h5"
									/>
								</CardBody>
							</Card>
						</Col>
						{/* Payments & Taxes */}
						<Col lg="6" className="mb-3">
							<Card>
								<CardHeader>
									<CardTitle tag="h3">Payments & Taxes</CardTitle>
								</CardHeader>
								<CardBody>
									<ReviewRow
										isCurrency
										label="Base Payment"
										value={lease.basePayment}
									/>
									<ReviewRow
										isCurrency
										label="Monthly Tax"
										value={lease.monthlyTax}
									/>
									<ReviewRow isPercent label="Tax Rate" value={sale.taxRate} />

									{/* Emphasize Monthly Payment (in-place) */}
									<EmphasisRow
										label="Monthly Payment"
										valueNode={fCurrency(lease.monthlyPmt)}
										rightClass="text-primary"
										sizeClass="h5"
									/>

									{/* Emphasize Total Rent */}
									<ReviewRow
										isCurrency
										label="Rent Charge"
										value={lease.totalRent}
									/>
									<ReviewRow
										isCurrency
										label="Depreciation"
										value={lease.depreciation}
									/>
									{/* the rest remain standard */}
									<EmphasisRow
										label="Total Base Payment "
										valueNode={fCurrency(lease.totalBase)}
										sizeClass="h6"
									/>
									<ReviewRow
										isCurrency
										label="Lease Tax"
										value={lease.totTax ?? lease.totalTax}
									/>
									{/* Emphasize Total Lease */}
									<EmphasisRow
										label="Total Lease"
										valueNode={fCurrency(lease.totalLease)}
										rightClass="text-success"
										sizeClass="h5"
									/>
									<EmphasisRow
										label="Total Drive-Off "
										valueNode={fCurrency(lease.driveOff)}
										rightClass="text-warning"
										sizeClass="h5"
									/>
									<EmphasisRow
										label="Total Payments"
										valueNode={fCurrency(lease.driveOff + lease.totalLease)}
										bg="#f4f4f6"
										rightClass="text-dark"
										sizeClass="h5"
									/>

									{/* <EmphasisRow
										label="Total Rent Charge"
										valueNode={fCurrency(lease.totalRent ?? lease.rentCharge)}
										bg="#f0f7ff"
										rightClass="text-info"
										sizeClass="h6"
									/> */}
									<ReviewRow
										isCurrency
										label="Pro-Rata Rent"
										value={lease.proRataRent}
									/>
									<ReviewRow
										isCurrency
										label="Pro-Rata Sales Tax"
										value={lease.proRataSalesTax}
									/>
								</CardBody>
							</Card>
						</Col>
						<Col lg="6" className="mb-3">
							<Card>
								<CardHeader>
									<CardTitle tag="h3">Drive-Off & Fees</CardTitle>
								</CardHeader>
								<CardBody>
									{sale.down > 0 && (
										<ReviewRow isCurrency label="Down" value={sale.down} />
									)}
									{/* {lease.lsRebate > 0 && (
										<ReviewRow
											isCurrency
											label="MFR Rebate"
											value={lease.lsRebate}
										/>
									)} */}
									{sale.totalNetTrade > 0 && (
										<ReviewRow
											isCurrency
											label="Net Trade"
											value={sale.totalNetTrade}
										/>
									)}
									{/* {!sale.totalPickupPmts > 0 && (
										<ReviewRow
											isCurrency
											label="Pickup Pmts"
											value={sale.totalPickupPmts}
										/>
									)} */}
									{lease.securityDeposit > 0 && (
										<ReviewRow
											isCurrency
											label="Security Deposit"
											value={lease?.securityDeposit ?? 0}
										/>
									)}
									{!lease.capDocFee && (
										<ReviewRow
											isCurrency
											label="Doc Fee"
											value={lease.docfee ?? sale.docFee}
										/>
									)}
									{!lease.capBankFee && (
										<ReviewRow
											isCurrency
											label="Bank Fee"
											value={lease.bankFee ?? sale.bankFee}
										/>
									)}
									{!lease.capDMVFee && (
										<ReviewRow
											isCurrency
											label="DMV Fee"
											value={sale.totalDMVFees}
										/>
									)}
									{lease.gpsService > 0 && (
										<ReviewRow
											isCurrency
											label="GPS Service"
											value={lease.gpsService}
										/>
									)}
									{!lease.capWarranty && (
										<ReviewRow
											isCurrency
											label="Warranty"
											value={lease.warranty ?? sale.warranty}
										/>
									)}
									{!lease.capOtherfee && (
										<ReviewRow
											isCurrency
											label="Other Fee"
											value={lease.otherFee}
										/>
									)}
									{!lease.capDispositionFee && (
										<ReviewRow
											isCurrency
											label="Disposition Fee"
											value={lease.disposition}
										/>
									)}
									{!lease.taxAmtInDriveOff > 0 && (
										<ReviewRow
											isCurrency
											label="Tax in Drive-Off"
											value={lease.taxAmtInDriveOff}
										/>
									)}
									{lease.downTax > 0 && (
										<ReviewRow
											isCurrency
											label="Tax on Reduction"
											value={lease.downTax}
										/>
									)}
									{!lease.capSalesTax && (
										<ReviewRow
											isCurrency
											label="Total Tax"
											value={lease.totTax}
										/>
									)}

									{!lease.capFirstPmt && (
										<ReviewRow
											isCurrency
											label="First Pmt"
											value={lease.monthlyPmt}
										/>
									)}
									{/* Emphasize Drive-Off Total */}
									<EmphasisRow
										label="Total Drive-Off"
										valueNode={fCurrency(lease.driveOff)}
										rightClass="text-warning"
										sizeClass="h5"
									/>
								</CardBody>
							</Card>
						</Col>
					</Row>

					{/* Drive-Off & Recap */}
					<Row></Row>
				</CardBody>
			</Card>
		</div>
	);
};

LeaseReview.propTypes = {
	onPrint: PropTypes.func,
};

export default LeaseReview;
